import { Grid, Paper } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router';
import { PerformanceCommentCategoryForm } from '.';
import * as api from '../../api/app/performanceCommentCategoryApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { AssetFullPathHeader } from '../../components/core/AssetFullPathHeader/AssetFullPathHeader';
import { Loader } from '../../components/core/Loader/Loader';
import { PageContent } from '../../components/core/PageContent/PageContent';
import { AssetDetailTabValues } from '../../types/app/asset.types';
import {
  PerformanceCommentCategory,
  PerformanceCommentCategoryInputs,
} from '../../types/app/peformanceCommentCategory.types';
import { dictionary } from '../../utils/core/dictionary';
import { convertDataFromInputToUpdateRequest } from '../../utils/core/functions/performanceCommentCategory.functions';
import { paths } from '../../utils/core/routes';
import { useStyles } from './PerformanceCommentCategoryForm.styles';

export function PerformanceCommentCategoryDetail() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id, parentId } = useParams();
  const [defaultValues, setDefaultValues] = useState<PerformanceCommentCategoryInputs | null>(null);

  const { mutate: mutateUpdateCommentCategory } = useMutation(api.updateItem);
  const { data, isLoading, isError } = useQuery<PerformanceCommentCategory>(
    ['commentCategory', id],
    () => api.getItem(Number(id)),
  );

  useEffect(() => {
    if (!data) return;

    setDefaultValues({
      id: data.id,
      name: data.name,
      assetId: data.assetId,
      assetName: data.assetName,
      orderingIndex: data.orderingIndex,
    });
  }, [data]);

  const handleOnSubmit: SubmitHandler<PerformanceCommentCategoryInputs> = (
    data: PerformanceCommentCategoryInputs,
  ) => {
    if (!defaultValues) return;

    const requestData = convertDataFromInputToUpdateRequest({
      ...data,
      id: Number(id!),
    });
    mutateUpdateCommentCategory(
      { ...requestData, id: Number(id) },
      {
        onSuccess: () => {
          enqueueSnackbar(dictionary.PERFORMANCE_COMMENT_CATEGORY_EDITED, { variant: 'success' });
          navigate(`${paths.ASSETS_DETAIL}/${parentId}`, {
            state: {
              tabValue: AssetDetailTabValues.PERFORMANCE_COMMENT_CATEGORY,
            },
          });
        },
        onError: (result) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  if (isError) return <ApiError />;
  if (isLoading || !defaultValues) return <Loader />;

  return (
    <>
      <AssetFullPathHeader
        finalTitle={dictionary.PERFORMANCE_COMMENT_CATEGORY_EDIT}
        parentId={Number(parentId)}
      />
      <PageContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8} lg={4}>
            <Paper className={classes.paper}>
              <PerformanceCommentCategoryForm
                onSubmit={handleOnSubmit}
                onBack={() =>
                  navigate(`${paths.ASSETS_DETAIL}/${parentId}`, {
                    state: {
                      tabValue: AssetDetailTabValues.PERFORMANCE_COMMENT_CATEGORY,
                    },
                  })
                }
                defaultValues={defaultValues}
              />
            </Paper>
          </Grid>
        </Grid>
      </PageContent>
    </>
  );
}
